import React, { useState, useMemo, useEffect } from 'react';
import { Search, Filter, Download, ChevronLeft, ChevronRight, Users, Plus, Edit, Trash2, Shield, ShieldOff } from 'lucide-react';
import { User } from '../../models/User';
import { UserActivity as UserActivityType, AdminActivityFilter, AdminActivityResponse } from '../../models/AdminActivity';
import { getUserActivities } from '../../data/adminActivityDatabase.ts';
import { 
  formatTimestamp, 
  formatActivityAction, 
  generatePageNumbers, 
  exportToCSV, 
  searchActivities,
  getActivityDetails,
  formatDateForFilename,
  getActionIcon as getSharedActionIcon,
  getActionColor as getSharedActionColor
} from '../../utils/adminUtils';
import { MetricCard } from './MetricCard';

interface UsersActivityProps {
  user: User;
}

function UsersActivity({ user }: UsersActivityProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<25 | 50 | 100 | 250>(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [activitiesData, setActivitiesData] = useState<AdminActivityResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load data with pagination
  useEffect(() => {
    const loadActivities = async () => {
      setIsLoading(true);
      try {
        const data = await getUserActivities(currentPage, pageSize);
        setActivitiesData(data);
      } catch (error) {
        console.error('Failed to load user activities:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadActivities();
  }, [currentPage, pageSize]);

  const userActivities = activitiesData?.activities as UserActivityType[] || [];
  const pagination = activitiesData?.pagination || { totalItems: 0, totalPages: 0, currentPage: 1, pageSize: 25 };

  // Apply filters
  const filteredActivities = useMemo(() => {
    let filtered = userActivities;
    
    if (actionFilter !== 'all') {
      filtered = filtered.filter(activity => activity.action === actionFilter);
    }
    
    return searchActivities(filtered, searchTerm) as UserActivityType[];
  }, [userActivities, searchTerm, actionFilter]);

  const handleExport = () => {
    exportToCSV(filteredActivities, `users-activities-${formatDateForFilename(new Date())}.csv`);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: 25 | 50 | 100 | 250) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const getActionIcon = (action: string) => getSharedActionIcon(action, 'user');
  const getActionColor = (action: string) => getSharedActionColor(action, 'user');

  // Calculate statistics
  const createActions = userActivities.filter(a => a.action === 'create').length;
  const updateActions = userActivities.filter(a => a.action === 'update').length;
  const deleteActions = userActivities.filter(a => a.action === 'delete').length;
  const adminActions = userActivities.filter(a => 
    a.action.includes('admin') || a.action.includes('super_admin')
  ).length;
  const suspensionActions = userActivities.filter(a => 
    a.action === 'suspend' || a.action === 'unsuspend'
  ).length;

  return (
    <div className="space-y-6">
      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <MetricCard
          title="Created Users"
          value={createActions}
          icon={Plus}
          color="green"
        />
        <MetricCard
          title="Updated Users"
          value={updateActions}
          icon={Edit}
          color="blue"
        />
        <MetricCard
          title="Admin Changes"
          value={adminActions}
          icon={Shield}
          color="purple"
        />
        <MetricCard
          title="Suspensions"
          value={suspensionActions}
          icon={ShieldOff}
          color="yellow"
        />
        <MetricCard
          title="Total Activities"
          value={pagination.totalItems}
          icon={Users}
          color="indigo"
        />
      </div>

      {/* Controls */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Search and Filters */}
            <div className="flex-1 flex flex-col sm:flex-row gap-4">
              <div className="flex-1 max-w-lg">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Search by user, target user, or user ID..."
                  />
                </div>
              </div>
              
              <select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                className="block w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="all">All Actions</option>
                <option value="create">Created</option>
                <option value="update">Updated</option>
                <option value="delete">Deleted</option>
                <option value="suspend">Suspended</option>
                <option value="unsuspend">Unsuspended</option>
                <option value="promote_admin">Promoted to Admin</option>
                <option value="demote_admin">Demoted from Admin</option>
                <option value="promote_super_admin">Promoted to Super Admin</option>
                <option value="demote_super_admin">Demoted from Super Admin</option>
              </select>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleExport}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Activities Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Users Activities ({pagination.totalItems})
            </h3>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value) as 25 | 50 | 100 | 250)}
              className="block w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
              <option value={250}>250 per page</option>
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Performed By
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Target User
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Target User ID
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredActivities.map((activity) => (
                  <tr key={activity.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionColor(activity.action)}`}>
                        {getActionIcon(activity.action)}
                        <span className="ml-1">{formatActivityAction(activity)}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {activity.userName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatTimestamp(activity.timestamp)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {activity.targetUserName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                      {activity.targetUserId}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {getActivityDetails(activity)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(Math.min(pagination.totalPages, currentPage + 1))}
                  disabled={currentPage >= pagination.totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * pageSize, pagination.totalItems)}
                    </span>{' '}
                    of <span className="font-medium">{pagination.totalItems}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                      disabled={currentPage <= 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    {generatePageNumbers(currentPage, pagination.totalPages).map((pageNum, index) => (
                      pageNum === -1 ? (
                        <span key={`dots-${index}`} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>
                      ) : (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === pageNum
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      )
                    ))}
                    <button
                      onClick={() => handlePageChange(Math.min(pagination.totalPages, currentPage + 1))}
                      disabled={currentPage >= pagination.totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UsersActivity; 